import type * as React from 'react'

// Epic 52 Phase 07 — the bell COMPOSITE. Manifest entry `icon` (131:8978) is the composite
// frame, not an export target itself; its two children are the real nodes:
//   Type=Normal            131:8858
//   Type=Has Notification  131:8979
// (confirmed via get_metadata(131:8978) — the manifest only lists the frame, per
// ICON-NORMALISATION.md §(a)'s table). Byte-compared the two exports' `<path id="Vector">` `d`
// strings: identical (1363 chars, both variants) — one shared bell body, not two independent
// icons. Shipped as ONE component with a `hasNotification` boolean prop, not two files.
//
// Step 7 (currentColor swap) applied selectively, not blindly: `Type=Has Notification`'s kept
// subtree has TWO distinct hexes — the bell body `#3B45E6` and a status-colour dot `#FF0000`
// (`<circle id="Notification Circle">`) — which is exactly the documented "more than one
// distinct hex" case that stops the blind full-subtree swap (§(b) Step 7 / §(e)). The bell body
// is genuinely brand-colour (same hex as every other single-hue icon in this set, matches
// Icon/Primary/Default) so it becomes `currentColor`; the red dot is a status colour, not a
// recolourable UI stroke, and stays literal so a `text-*` colour change on the icon can never
// silently wash out the notification indicator.
type SvgIconProps = Omit<React.SVGProps<SVGSVGElement>, 'children'> & {
  /** Accessible name. Omit for a purely decorative icon (default: aria-hidden). */
  title?: string
}

export function NotificationIcon({
  hasNotification,
  title,
  ...rest
}: SvgIconProps & { hasNotification?: boolean }) {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8 1.25C9.39236 1.25002 10.7274 1.80354 11.7119 2.78809C12.6965 3.77265 13.25 5.10763 13.25 6.5C13.25 8.61365 13.7372 9.78166 14.0801 10.374L14.0811 10.375C14.1632 10.5173 14.2159 10.6741 14.2373 10.8359L14.248 10.999L14.2373 11.1621C14.2161 11.3239 14.1639 11.4807 14.082 11.623C13.9728 11.8129 13.8154 11.971 13.626 12.0811C13.4367 12.191 13.2218 12.2492 13.0029 12.25H10.7373C10.6791 12.8876 10.4008 13.4879 9.94434 13.9443C9.42861 14.4601 8.72935 14.75 8 14.75C7.27066 14.75 6.57139 14.4601 6.05567 13.9443C5.59923 13.4879 5.32095 12.8876 5.2627 12.25H2.99512C2.7765 12.2486 2.5619 12.1902 2.37305 12.0801C2.18421 11.9699 2.02776 11.8117 1.91895 11.6221C1.81013 11.4323 1.7526 11.2168 1.75293 10.998L1.76367 10.835C1.78519 10.6734 1.83887 10.5171 1.9209 10.375L2.05469 10.125C2.3773 9.47556 2.75 8.34942 2.75 6.5C2.75 5.10761 3.30352 3.77265 4.28809 2.78809C5.27265 1.80354 6.60763 1.25 8 1.25ZM6.77539 12.25C6.82408 12.4883 6.94163 12.7092 7.11621 12.8838C7.35063 13.1182 7.66848 13.25 8 13.25C8.33152 13.25 8.64937 13.1182 8.88379 12.8838C9.05838 12.7092 9.17592 12.4883 9.22461 12.25H6.77539ZM8 2.75C7.00546 2.75 6.05189 3.1454 5.34863 3.84863C4.64537 4.55189 4.25 5.50544 4.25 6.5C4.25 8.54065 3.8388 9.88484 3.41699 10.75H12.584C12.1616 9.88485 11.75 8.54107 11.75 6.5C11.75 5.50546 11.3546 4.55189 10.6514 3.84863C9.94813 3.14539 8.99453 2.75002 8 2.75Z"
        fill="currentColor"
      />
      {hasNotification ? <circle cx="13" cy="3" r="3" fill="#FF0000" /> : null}
    </svg>
  )
}
